import React from 'react';
import { Tooltip } from 'antd';
import { Icon, Text } from '@common/components';
import { Spacing } from '@common/tokens';

export type KpiKeyType =
  | 'fillRate'
  | 'loadBalance'
  | 'fteUtilization'
  | 'preferenceMatch';

export type KpiRowType = {
  key: KpiKeyType;
  label: string;
  tooltip: string;
  pre: number | null;
  balanced: number | null;
  current: number | null;
};

export function KpiChip({ row }: { row: KpiRowType }) {
  const delta = computeDelta(row.current, row.balanced);
  return (
    <Tooltip
      title={
        <Text size="md" color="white">
          {row.tooltip}
        </Text>
      }
    >
      {/* A host element keeps the antd Tooltip's ref target — Gap/Flex don't
          forward refs — so the chip lays out with inline-flex here. */}
      <span className="schedule-health-strip__chip">
        <Text size="md" weight="medium" color="midnightRegular">
          {row.label}
        </Text>
        <Text size="md" bold color="midnightDarkest">
          {formatPercentage(row.current)}
        </Text>
        {delta !== null && delta !== 0 && <DeltaBadge delta={delta} />}
        <style jsx>{`
          .schedule-health-strip__chip {
            display: inline-flex;
            align-items: center;
            gap: ${Spacing.sm}px;
            white-space: nowrap;
          }
        `}</style>
      </span>
    </Tooltip>
  );
}

export function KpiTable({ rows }: { rows: ReadonlyArray<KpiRowType> }) {
  return (
    <div className="schedule-health-strip__table">
      <span aria-hidden="true" />
      <Text size="md" weight="medium" color="midnightRegular">
        Pre-balance
      </Text>
      <Text size="md" weight="medium" color="midnightRegular">
        Auto-balanced
      </Text>
      <Text size="md" weight="medium" color="midnightRegular">
        Current
      </Text>
      <Text size="md" weight="medium" color="midnightRegular">
        Your edits
      </Text>
      {rows.map((row) => (
        <KpiTableRow key={row.key} row={row} />
      ))}
      <style jsx>{`
        .schedule-health-strip__table {
          display: grid;
          /* Pin columns to their content so values sit close to labels
             instead of stretching across the full-width strip. */
          grid-template-columns: repeat(5, max-content);
          column-gap: ${Spacing.xl}px;
          row-gap: ${Spacing.xs}px;
          align-items: center;
          justify-content: start;
        }
      `}</style>
    </div>
  );
}

function KpiTableRow({ row }: { row: KpiRowType }) {
  const delta = computeDelta(row.current, row.balanced);
  return (
    <>
      <Tooltip
        title={
          <Text size="md" color="white">
            {row.tooltip}
          </Text>
        }
      >
        <span>
          <Text size="md" color="midnightRegular">
            {row.label}
          </Text>
        </span>
      </Tooltip>
      <Text size="md" color="midnightRegular">
        {formatPercentage(row.pre)}
      </Text>
      <Text size="md" color="midnightRegular">
        {formatPercentage(row.balanced)}
      </Text>
      <Text size="md" bold color="midnightDarkest">
        {formatPercentage(row.current)}
      </Text>
      {delta === null || delta === 0 ? (
        <Text size="md" color="midnightLighter">
          &mdash;
        </Text>
      ) : (
        <DeltaBadge delta={delta} />
      )}
    </>
  );
}

function DeltaBadge({ delta }: { delta: number }) {
  const positive = delta > 0;
  const color = positive ? 'grassDarkest' : 'coral';
  return (
    <span
      className="schedule-health-strip__delta"
      aria-label={`${positive ? 'Up' : 'Down'} ${Math.abs(delta)} percent vs auto-balanced`}
    >
      <Icon
        icon={positive ? 'chevronUp' : 'chevronDown'}
        kind="solid"
        size="sm"
        color={color}
      />
      <Text size="sm" bold color={color}>
        {Math.abs(delta)}%
      </Text>
      <style jsx>{`
        .schedule-health-strip__delta {
          display: inline-flex;
          align-items: center;
          gap: ${Spacing.xs}px;
          white-space: nowrap;
        }
      `}</style>
    </span>
  );
}

// "Your edits": the live current value minus auto_balanced. Both operands come from the
// server response (currentSummary + snapshotSummary.autoBalanced); the subtraction is trivial
// so it stays client-side.
function computeDelta(
  current: number | null,
  balanced: number | null
): number | null {
  if (current === null || balanced === null) return null;
  return Math.round(current - balanced);
}

function formatPercentage(value: number | null): string {
  if (value === null) return '—';
  return `${Math.round(value)}%`;
}
